import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import axios from 'axios';
import { IS_DEMO, IS_LOCKED } from '../../apiBase';
import {
  Loader2, ZoomIn,
} from 'lucide-react';
import { isHardwareOrTool, isLeftRight, usePartsRun, type PartItem } from './partsRun';
import { interactionsOfPart, useHardwareInteractions } from './hardwareInteractionsRun';
import type { WrittenInteraction } from './openaiBlenderApi';
import { toolInteractions, useDriven } from './toolPairs';
import { partBuildMaterials, useToolDriven } from './toolSet';
import type { PartProfile } from './partProfile';
import { useImagesEpoch } from './imagesRefresh';
import { useCatalogItems } from './catalogItems';
import { useCategories } from './catalogCategories';
import type { PictureSource } from './pictureRef';
import { partBuildSize } from './partIdentity';
import { partPhotoSrc, usePartPhotos, type PartPhoto } from './partPhotos';
import { directionLabel } from './viewDirections';
import { WrittenCallBadge } from './WrittenCallBadge';
import { invalidatePartModels } from './partModels';
import { useSubmodelFilter } from './submodelFilterStore';
import { useCatalogSwitch, type CatalogSwitch } from './catalogSwitch';
import { VIEWER_BOX } from './PartViewer';
import { DeleteRunButton, MeshStage } from './MeshStage';
import { MeshPanel, panelTitle } from './MeshPanel';
import { MeshRunStrip, useShownFailure, useShownRun } from './MeshRunStrip';
import { usePublishShownMesh } from './shownMesh';
import { useMaterialsRun, type Material } from './materialsRun';
import { MeshGenerateControls } from './MeshGenerateControls';
import { FailedRunPanel } from './FailedRunPanel';
import { MeshPlaceholder } from './MeshPlaceholder';
import { MeshControls } from './MeshControls';
import { CropStrip, type StripCrop } from './CropStrip';
import { usePartStepCrops, type StepCropPicture } from './hardwareCropsRun';
import {
  repaintKept, usePartMaterialNames, usePartMaterials, usePartMaterialsAlso,
} from './partMaterialRun';
import { paintedAs, surfaceOf, useMaterialSurfaces } from './materialSurface';
import { ImageLightbox } from './ImageLightbox';
import {
  NO_CROP_PICKS, NO_PICKS, endRun, getGeneratorChoice, runInFlight, setGeneratorChoice, startRun,
  setRunProgress, useGeneratorChoice, useRunsInFlight,
} from './generatorMemory';
import { GeneratedDetails } from './GeneratedDetails';
import { useGeneratedPhotos, type HeroBox } from './realImages';
import { keepDrawing, useItemSchematics } from './catalogItems';
import { usePartCrops } from './partCrop';
import { schematicSrc } from './panels/CatalogItemRow';
import {
  defaultPasses, engineLabelOf, writtenEngineOf, writtenModelLabel, generateWrittenMesh,
  engineModelKey, isWrittenRun, newRunToken, passChoices, generationTook, generationStages, passGroup,
  builtBoxOf, runSurfaceOf, stopWritten, writtenInputsOf, writtenModelKeys, writtenSize,
  writtenSizeOf,
  type RunProgress, type WrittenInput, defaultEffort, effortChoices, withEffort, dimensionsOf, checksOf,
} from './openaiBlenderApi';
import {
  CAD_MODEL, LATHE_MODEL, colourJob, deleteJob, editsOf, errorOnARow, falError, fetchJobs,
  fetchStatus3D, meshSrc, meshAtSrc, meshStamp, rescaleJob,
  isTrueScale, pieceOf,
  type Job3D, type Status3D,
} from './threeDApi';
import { type AxisFit, type Extent } from './meshAnnotations';
import { useLanguage } from '../../contexts/LanguageContext';
import { WrittenDimensionsPanel } from './WrittenDimensionsPanel';
import { PICKED } from './pickedLook';

// THE MANUAL'S OWN DRAWING OF A PART, among the pictures it can be built from: not a stored
// picture yet, so no id of one. The press stores it (see keepDrawing) and sends that id.
const DOC_DRAWING = -1;
const NO_PARTS: PartItem[] = [];


// The OPEN part's mesh, written by a model from its Parts-DB pictures. Which part that is
// belongs to PartsPhase, which draws the selector above this and the table it opens — a mesh
// is one more thing known about the part on screen, not a view of its own with a list of its
// own.
//
// The inputs are the PARTS DATABASE, not the generation history. Every attempt is kept
// now, good and bad, and the database is the subset somebody switched on. A run believes
// whatever it is shown.

// A part, its catalog item, and the photographs the database holds of it.
type PartRow = {
  key: string;
  name: string;
  itemId: number;
  // Every photograph of the part, in the order the database holds them — what the run's
  // one input is chosen from.
  photos: PartPhoto[];
  // The one picked by default: the picture recorded as the part's front.
  front: PartPhoto | null;
  // The part's real size, read off the listing's dimension drawing in Phase 2. Carried
  // here only to be drawn around the mesh: a reconstruction comes back normalised to
  // roughly a unit, so this is the one place a viewer can say how big the thing is.
  size: PartItem['size'];
  // What the part IS, for the controls that only apply to some kinds of part. A drive
  // recess is a thing a barrel nut has and a chair leg does not.
  category: string | null;
  // WHETHER THE MANUAL SUPPLIES THIS PART IN TWO HANDS — a left/right mirror pair, read off
  // Phase 2's own answer (see symmetryOf). Carried on the row because the viewer draws the
  // toggle and the viewer is handed the row: it is the same kind of fact as the size, a thing
  // the manual said that only the picture can act on.
  pair: boolean;
};

const frontOf = (photos: PartPhoto[]): PartPhoto | null =>
  photos.find((p) => p.view === 'front') ?? null;

export function ThreeDStep({ docId, file, locked, part, partAt, itemId, partName, corner, badgeOnly }: {
  docId: string;
  // The manual, for a piece of hardware's page crops — cut from pages rendered off it here.
  file: Blob;
  locked?: boolean;
  // Where this part sits in the notice's list — how every per-part answer in the phase is
  // keyed, including the listing crops a colour can now be read from.
  partAt?: number;
  // The open part, its catalog row (null when the database has never seen it — then there
  // is nothing to hang a photograph or a mesh off) and the name the selector calls it.
  part: PartItem;
  itemId: number | null;
  partName: string;
  // At the top of the card: which model of the subphase is on screen (see PartsPhase's modelPicker).
  corner?: ReactNode;
  // Only its generation badge, nothing on the page — see PartsPhase's hwView.
  badgeOnly?: boolean;
}) {
  const { t } = useLanguage();
  const submodelFilter = useSubmodelFilter(docId);
  // WHAT THE OPEN PART IS MADE OF, and what that material looks like — for painting a mesh
  // the moment it is made. Both are the phase's own answers: the parts table's material, and
  // the colour somebody sampled for it off a photograph.
  const submodel = submodelFilter;
  const partMaterials = usePartMaterials(docId);
  useMaterialSurfaces(docId);
  // WHAT THE OPEN PART IS MADE OF AND WHAT THAT LOOKS LIKE, worked out once here rather than
  // by each control that needs it. Painting a new mesh wants it, and so does levelling a face
  // — a face is corrected to the material's own colour where one has been measured, and this
  // is where that colour lives.
  const worn = partAt == null ? null : partMaterials.bySub[submodel]?.[partAt] ?? null;
  const wornSurface = worn ? surfaceOf(docId, worn) : null;
  // A TOOL ON SEVERAL MATERIALS IS BUILT WITH THEM — see partBuildMaterials.
  usePartMaterialsAlso(docId);
  usePartMaterialNames(docId);
  const builtWith = (): Material[] | null => (partAt == null ? null
    : partBuildMaterials(docId, submodel, partAt, t('notice.3d.buildNoSurface')));

  // The generator's database switch bumps this, and this half's inputs are precisely what
  // that switch changes — so the two stay in step without a second refresh mechanism.
  const epoch = useImagesEpoch(docId);
  // The Parts-DB photographs of THIS notice, for the submodel on screen — the curated set
  // a reconstruction is made from. Filtered to the open part below.
  const { photos, loaded: photosLoaded } = usePartPhotos(docId, submodelFilter, epoch);

  // Which generated attempt each Parts-DB photograph came from. The two are the same
  // picture matched on its bytes — see useGeneratedPhotos, which the generation table
  // beside this one asks from the other end.
  const { entryOf } = useGeneratedPhotos(docId);
  // The part's drawing, for the provenance panel: every generation was given it as its
  // first image, so the row that says what was sent is wrong without it. From the catalog
  // rather than redrawn from the PDF, this panel having no page to crop.
  const schematics = useItemSchematics(itemId);
  const partSchematic = schematics.length ? schematicSrc(schematics[0].id) : undefined;

  // Whether anything can run at all, and what a run costs: /api/notice/3d/status.
  const [probe, setProbe] = useState<Status3D | null>(null);
  // Whether the probe has answered, either way: the runs are asked for once it has — see below.
  const [probed, setProbed] = useState(false);
  // EVERY RUN OF THESE ENGINES, NULL UNTIL THE SERVER HAS ANSWERED. It started as `[]` — the
  // same value as "there are none" — so for a round trip every part said it had no mesh.
  // `jobsFailed`: the last ask did not come back, said as a failure and never drawn as none.
  const [jobs, setJobs] = useState<Job3D[] | null>(null);
  const [jobsFailed, setJobsFailed] = useState(false);
  // WHICH PART A RUN IS MAKING, if any — kept outside this panel, which is thrown away whenever
  // the product or "All parts" is opened. See generatorMemory: a run left behind comes back with
  // its spinner and its Stop. One part at a time, as it always was.
  const flights = useRunsInFlight(docId);
  const partRunning = Object.keys(flights).find((k) => k.startsWith('part:'));
  const busyKey = partRunning ? partRunning.slice('part:'.length) : null;
  const [error, setError] = useState('');
  const [zoom, setZoom] = useState<string | null>(null);
  // WHAT THE ARROWS WALK while one is zoomed: the tiles the zoom was opened from — the photographs
  // and the manual's drawings beside them — not only the photographs.
  const [zoomList, setZoomList] = useState<string[] | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  // A PIECE OF HARDWARE IS DRAWN, NOT PHOTOGRAPHED — the whole of what changes below. The
  // ontology's answer, except that a part FLAGGED as a tool is a tool whatever folder it sits
  // in: the same test the parts phase and the sub-step chips ask, asked of the same two facts.
  const ontology = useCategories();
  const schema = isHardwareOrTool(part, ontology);
  // A COMPONENT PART'S MESH COMES FROM THE WHOLE PRODUCT AND FROM NOTHING ELSE — cut out of it
  // when the product is kept (see _keep_pieces). So it has no way of making one here: no
  // generation, and only the runs that are pieces of a product.
  const piecesOnly = !schema;
  // AND WHERE THE MANUAL DRAWS IT IN USE, for a piece of hardware: its Phase 2 page crops,
  // offered beside the drawing as more pictures to build from.
  const stepCrops = usePartStepCrops(docId, schema ? file : null, partAt);
  // AND THE HARDWARE IT WORKS WITH, off Phase 4's hardware table — what orients its build
  // against theirs (see interactionsOfPart). Empty for a component, which is oriented by the
  // product it is a piece of.
  const { items: allParts } = usePartsRun(docId);
  // AND THIS MANUAL'S OWN DRAWING OF IT — the one its card shows, cut from the page — which is
  // what it is built from (see DOC_DRAWING). Null for a part the manual draws no box round.
  const docDrawings = usePartCrops(file, schema ? allParts : NO_PARTS);
  const docDrawing = schema && partAt != null ? docDrawings[partAt] ?? null : null;
  const table = useHardwareInteractions(docId);
  // A TOOL IS IN NO ROW OF THAT TABLE — "a tool is never hardware" there — and is told instead what
  // it DRIVES, off the sub-steps that use it (see toolPairs). Only here where that hardware has no
  // catalog row: a tool that can be is built WITH it, as a set (see toolSet), and not on this card.
  const driven = useDriven(docId, part?.tool ? partAt : null);
  // AND A PIECE OF HARDWARE A TOOL DRIVES MAKES NO MODEL HERE: it is made in that tool's set, its
  // recess cut against the tip (see toolSet), and built here it would replace that one.
  const toolDriven = useToolDriven(docId);
  const toolMade = schema && partAt != null && toolDriven.has(partAt);
  const interactions = useMemo<WrittenInteraction[]>(
    () => (!schema || partAt == null ? []
      : part?.tool ? toolInteractions(driven)
        : interactionsOfPart(table.interactions, table.alone, partAt, allParts)),
    [schema, partAt, part?.tool, driven, table.interactions, table.alone, allParts],
  );

  useEffect(() => {
    void fetchStatus3D().then(setProbe).catch(() => setProbe(null)).finally(() => setProbed(true));
  }, []);

  // THE PART'S RUNS, under whichever written model made them. AND THE CAD UPLOADS AND TURNED MESHES OF BEFORE, which are no longer
  // made here — every model is generated now — but are still runs, some of them kept, and a kept
  // model has to be on the strip to be seen and taken out.
  //
  // Memoised on the probe, which is fetched once: `jobModel` is a dependency of the fetch that
  // fills the panel, and an array rebuilt every render would refetch every render, for ever.
  const writtenKeys = useMemo(() => writtenModelKeys(probe), [probe]);
  const jobModel = useMemo(
    () => [...writtenKeys, CAD_MODEL, LATHE_MODEL],
    [writtenKeys],
  );
  // ONLY THE NEWEST ANSWER LANDS. Two of these used to be in flight on every mount, because
  // the list of models to ask for is not known until the probe says which written models
  // exist, and whichever reply arrived last won. The first ask now waits for the probe (see
  // below), and a list of half the models is never on screen.
  //
  // A sequence and not an `alive` flag in the effect, because reloadJobs is also called by
  // hand — after a run, a delete, a repair — and two of those overlapping race in exactly
  // the same way for the same reason. A failed reload keeps what was known and says so.
  const jobsSeq = useRef(0);
  const reloadJobs = useMemo(
    () => () => {
      const seq = jobsSeq.current + 1;
      jobsSeq.current = seq;
      return fetchJobs(jobModel)
        .then((got) => { if (seq === jobsSeq.current) { setJobs(got); setJobsFailed(false); } })
        .catch(() => { if (seq === jobsSeq.current) setJobsFailed(true); });
    },
    [jobModel],
  );
  // AND READ AGAIN WHEN A RUN LANDS. The press that started it reloads when it finishes — but
  // that press may belong to a panel that was thrown away while it ran, whose reload reaches
  // nobody. So the run ending is heard here too, by whichever panel is on screen then.
  // AND WHEN THIS PART'S MATERIAL HAS BEEN WRITTEN ONTO ITS MESHES — the files changed under
  // the same ids, so the list is read again for their new versions. See followPartMaterials.
  const materialsRun = useMaterialsRun(docId);
  const repainting = materialsRun.applying.has(submodel) && itemId != null
    && materialsRun.applyingItems.has(itemId);
  const wasRepainting = useRef(repainting);
  useEffect(() => {
    if (wasRepainting.current && !repainting) void reloadJobs();
    wasRepainting.current = repainting;
  }, [repainting, reloadJobs]);
  const wasRunning = useRef(busyKey);
  useEffect(() => {
    if (wasRunning.current && !busyKey) void reloadJobs();
    wasRunning.current = busyKey;
  }, [busyKey, reloadJobs]);
  // ASKED ONCE THE PROBE HAS ANSWERED, and not emptied first: the list is every part's, so a
  // part switched to is answered by the list already here — blanking it made "no mesh" flash
  // over every part that had one.
  useEffect(() => {
    if (probed) void reloadJobs();
  }, [probed, reloadJobs]);



  // The open part, with the photographs the database holds of it — everything the card in
  // front of you draws from. Null when the catalog has never seen the part: there is no row
  // for a photograph or a mesh to hang off, which is a Parts-DB question and not this
  // half's, and the table above says so in its own row.
  const { items: catalog } = useCatalogItems();
  const row = useMemo<PartRow | null>(() => {
    if (itemId == null) return null;
    const mine = photos.filter((p) => p.itemId === itemId);
    return {
      key: `${itemId}`,
      name: partName,
      itemId,
      // AS THIS CATEGORY DESCRIBES IT. A part carries whatever any reading ever wrote on
      // it, including axes answered while it was filed somewhere a part is a bounding box
      // — and the viewer would label the mesh from those in preference to the diameter the
      // table is showing. See partBuildSize.
      size: partBuildSize(catalog, itemId, part),
      category: part.category ?? null,
      pair: isLeftRight(part),
      photos: mine,
      front: frontOf(mine),
    };
  }, [itemId, photos, part, partName, catalog]);

  // PAINT A WRITTEN RUN FROM THE PART'S MATERIAL. A model writing bpy invents its colours:
  // it looks at the photograph and picks a brown, and where it gives the parts of one frame
  // three different browns it has invented a finish nobody sells. The app has a colour
  // SAMPLED off a picture of the real thing, filed under the material this part is made of
  // — so the guess is replaced by the measurement, exactly as a turned part is painted the
  // moment it is made.
  //
  // Only where the material has been sampled. No colour and the mesh keeps what it was
  // given, rather than being painted something nobody has a reason to believe.
  //
  // ONCE, and a colour edit in the stack is how a run says so. Which makes this idempotent
  // — it can be asked of the same run all day — and leaves alone any run somebody has since
  // recoloured by hand, whose colour is a decision and not an omission.
  // WHETHER A WRITTEN RUN IS OWED ITS MATERIAL: never painted, or painted flat before its material
  // had a surface to draw (a default material's texture, asked afterwards) — which the page then
  // draws, once. Not a run whose last paint already carried one: the paint is an edit, and a sweep
  // that repainted on every visit would stack them.
  const owesPaint = (job: Job3D) => {
    const colours = editsOf(job).filter((e) => e.op === 'colour');
    if (!colours.length) return true;
    return !!wornSurface?.texture && !colours[colours.length - 1].texture;
  };
  const paintWritten = async (job: Job3D) => {
    if (!worn || !wornSurface?.rgb) return;
    if (!isWrittenRun(job)) return;

    // THE WOOD FIRST, THEN THE COLOUR, and the order is the whole of why both are worth
    // doing. A written mesh has no surface — no UVs, no texture, one flat colour the model
    // guessed — so painting it alone only trades an invented brown for a measured one. Give
    // it a photograph of the real wood and the paint has a figure to KEEP, which is exactly
    // what a reconstruction has always had and what `grain` in glb.painted is for.
    //
    // The patch is cut from the same picture the colour was sampled off, so the two halves
    // are the same material seen the same way.
    if (!owesPaint(job)) return;
    // THE RUN'S OWN READINGS FIRST, where it has them: a run made before materials were stated
    // had a patch of its photograph tiled on it, the colour measured off that patch, and a
    // roughness the model judged — all about THIS wood, where the material's are about the word
    // for it. The material's stand in wherever the run has nothing to say.
    const mine = runSurfaceOf(job);
    // GRAIN ON, whatever the material's stored preference says. Those earlier runs were born
    // with their wood on them, so the paint has a figure to keep, and flattening it would undo
    // the thing the call was arranged around. On a mesh that got none — every run since —
    // painted() reports that it kept nothing and the result is flat.
    try {
      await colourJob(job.id, mine.rgb ?? wornSurface.rgb, undefined, wornSurface.fromUrl,
        {
          ...paintedAs(worn, wornSurface),
          ...(mine.roughness != null ? { roughness: mine.roughness } : {}),
          ...(mine.metallic != null ? { metallic: mine.metallic } : {}),
          grain: true,
        });
    } catch (e) {
      setError(falError(e, t('notice.3d.failed')));
    }
  };

  // AND THE RUNS MADE BEFORE IT DID. The same rule, applied to what is already there: every
  // written run of this part that has never been painted, as soon as the material's colour
  // is known. It settles after one pass — the test above is what the pass changes — and the
  // paint is an edit, so any of it can be undone from the run it landed on.
  //
  // Per part, because it is that part's material that decides the colour. The ref holds
  // WHICH part has been swept rather than a flag, so opening the next one sweeps that one.
  const swept = useRef<number | null>(null);
  useEffect(() => {
    if (itemId == null || jobs === null || swept.current === itemId) return;
    // WHY IT IS NOT DOING ANYTHING, when it is not. These two are the conditions that stop
    // it before it starts, and both are facts about the notice rather than about the run —
    // a part with no material assigned in Phase 2, or a material nobody has picked a colour
    // for. Silence here reads as the feature being broken.
    // THIS PART'S RUNS, AND ONLY THIS PART'S. `jobs` is every run this engine has made in
    // this manual — the panel filters it into partJobs to draw, and the sweep read the raw
    // list. So opening one part painted every unpainted written run in the document with
    // that part's material: the other parts' and, worse, the whole product's, whose mesh has
    // several materials and comes back with all of them one colour (glb.painted sets every
    // material, which is right for the converted STEP file it was written for and wrong for
    // a chair). A chair's beige seat was brown for an hour because a rail was opened.
    const unpainted = (j: Job3D) => j.ready && isWrittenRun(j) && j.itemId === itemId && owesPaint(j);
    if (!worn || !wornSurface?.rgb) {
      if (jobs.some(unpainted)) {
        setError(t(worn ? 'notice.3d.paintNoColour' : 'notice.3d.paintNoMaterial'));
      }
      return;
    }
    const owed = jobs.filter(unpainted);
    if (!owed.length) return;
    swept.current = itemId;
    void (async () => {
      for (const job of owed) await paintWritten(job);
      await reloadJobs();
      invalidatePartModels();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, jobs, worn, wornSurface?.rgb, wornSurface?.texture]);

  // THE MODEL THAT WRITES IT — none on a server without an OpenAI key, whose button is dead.
  const writtenModel = writtenEngineOf(probe);
  // The effort and the passes, one choice for every part — see generatorMemory.
  const choice = useGeneratorChoice(docId, 'parts');
  // HOW MANY TIMES THE WRITTEN ENGINE MODELS IT. Here because the run is made here.
  const passes = choice.passes ?? defaultPasses(probe);
  const setPasses = (value: number) => setGeneratorChoice(docId, 'parts', { passes: value });
  // HOW HARD IT THINKS: what was chosen, where the chosen model takes it, else its default.
  const efforts = writtenModel ? effortChoices(probe, writtenModel) : [];
  const effort = choice.effort && efforts.includes(choice.effort)
    ? choice.effort
    : (writtenModel ? defaultEffort(probe, writtenModel) : '');
  const setEffort = (value: string) => setGeneratorChoice(docId, 'parts', { effort: value });

  // THE RUN IN FLIGHT, CALLED OFF.
  const stopRun = () => {
    const going = busyKey ? runInFlight(docId, `part:${busyKey}`) : null;
    if (!going?.token || !going.abort) return;
    // The server first, then the page: the POST is what prevents the next call, and
    // aborting is what gives the button back. Aborting first would sometimes drop the
    // request that does the useful half.
    void stopWritten(going.token).catch(() => {});
    going.abort.abort();
  };

  const run = async (key: string, inputs: WrittenInput[], stepCrops: string[] = []) => {
    // WHICH TABLE THE PICTURES ARE IN, which is the same question as what they ARE: a component
    // is modelled from photographs and a piece of hardware from the manual's own drawing.
    const source: PictureSource = schema ? 'schematic' : 'photo';
    if (busyKey || !inputs.length || !writtenModel) return;
    const runKey = `part:${key}`;
    // Before anything is out: a material with no surface yet is said, and nothing is built.
    let materials: Material[] | null;
    try {
      materials = builtWith();
    } catch (e) {
      setError((e as Error).message);
      return;
    }
    startRun(docId, runKey, { token: null, abort: null });
    setError('');
    // How many passes of this run the page has already read in — see onProgress below.
    let landed = 0;
    try {
      // THE MANUAL'S DRAWING IS KEPT ON THE PART'S ROW FIRST, where it is one of the pictures: a
      // build is handed a stored picture by id, and this is the press that stores it.
      const sent = await Promise.all(inputs.map(async (one) => {
        if (one.id !== DOC_DRAWING) return one;
        if (itemId == null || !docDrawing) throw new Error(t('notice.3d.failed'));
        return { ...one, id: await keepDrawing(itemId, docId, docDrawing) };
      }));
      // The part's NAME goes with the picture: a photograph of a rail is a rail either
      // way, but the name is free and it is the difference between "model this" and
      // "model this backrest and rear legs frame".
      const going = { token: newRunToken(), abort: new AbortController() };
      startRun(docId, runKey, going);
      const job = await generateWrittenMesh(source, sent, writtenModel, {
        subject: row?.name,
        stop: { token: going.token, signal: going.abort.signal },
        passes,
        // The part's own size, as its category describes it — see `row`.
        size: writtenSize(row?.size),
        effort: effort || undefined,
        stepCrops,
        interactions,
        ...(materials ? { materials } : {}),
        onProgress: (progress) => {
          setRunProgress(docId, runKey, progress);
          // EACH PASS AS IT LANDS. A run saves every pass the moment it is built, so the
          // strip can hold pass 2 while pass 3 is being thought about — and the panel
          // shows the newest, which is what it shows when nobody has chosen otherwise.
          // Only on the way UP: the list is re-read when the count grows, not per poll.
          if (progress.saved.length > landed) {
            landed = progress.saved.length;
            void reloadJobs();
          }
        },
      });
      // Painted before the list is read again, so it never appears in the guessed colour
      // and then change under whoever is looking at it.
      if (job) await paintWritten(job);
      await reloadJobs();
    } catch (e) {
      // A run somebody stopped is not a run that failed, and the red line under the button
      // would be reporting their own press back at them.
      if (!axios.isCancel(e)) setError(falError(e, t('notice.3d.failed')));
      await reloadJobs();
    } finally {
      endRun(docId, runKey);
    }
  };

  // KEEP A MESH AS THE PART'S 3D MODEL, or stop keeping it — see useCatalogSwitch. The answered
  // row is patched into the list rather than the whole history refetched: one bit changed on one
  // run, and a refetch would rebuild the list under the viewer that is showing it.
  const dbSwitch = useCatalogSwitch((saved) => {
    // ONE KEPT PER PART: the server un-keeps the others when one is kept (see set_in_catalog),
    // so the strip does the same rather than showing two lit until the next read.
    setJobs((list) => list && list.map((j) => (j.id === saved.id ? saved
      : saved.inCatalog && j.itemId === saved.itemId && j.inCatalog ? { ...j, inCatalog: false }
        : j)));
    if (saved.inCatalog) repaintKept(docId, submodel);
  }, setError);

  // Resize a mesh to one of its measured axes, in place. The answered row is patched in,
  // as a repair's is: the run keeps its id, so there is nothing new in the strip to
  // discover and a refetch would rebuild it under the viewer that is showing it.
  const [rescalingId, setRescalingId] = useState<number | null>(null);
  const rescale = async (job: Job3D, axis: 'x' | 'y' | 'z', fit: AxisFit) => {
    setRescalingId(job.id);
    setError('');
    try {
      const saved = await rescaleJob(job.id, { scale: fit.scale, axis, mm: fit.measured });
      setJobs((list) => list && list.map((j) => (j.id === saved.id ? saved : j)));
      return saved;
    } catch (e) {
      setError(falError(e, t('notice.3d.rescaleFailed')));
      return null;
    } finally {
      setRescalingId(null);
    }
  };

  const remove = async (job: Job3D) => {
    setDeletingId(job.id);
    try {
      await deleteJob(job.id);
      await reloadJobs();
      // Deleting the run that WAS the part's model takes the model with it, so the dots
      // and the bar have to be re-read rather than left showing a mesh that is gone.
      if (job.inCatalog) invalidatePartModels();
    } finally {
      setDeletingId(null);
    }
  };

  // What a converted run's file was called, or null when the run has no file behind it —
  // a reconstruction, or a conversion from before the name was recorded.
  const cadFilename = (job?: Job3D | null): string | null =>
    (job?.params?.cad as { filename?: string } | undefined)?.filename || null;

  // A CONVERTED FILE'S NAME, as the tooltip on its thumbnail — where its geometry came from.
  // Nothing on any other run: the strip's row already names the model that wrote it.
  const settingsLine = (job: Job3D) =>
    (job.params?.cad ? cadFilename(job) ?? t('notice.cad.title') : undefined);


  // The attempt behind the picture being zoomed, if it is one of this part's photographs
  // and the database can name it. A schematic or anything else zoomed here matches
  // nothing, which is what keeps that zoom plain.
  const zoomedEntry = zoom
    ? entryOf((row?.photos || []).find((p2) => partPhotoSrc(p2.id) === zoom)?.id)
    : undefined;

  // A RUN OF THIS PART: it empties the viewer and adds a tile to the strip.
  const generatingHere = !!row && busyKey === row.key;
  // Every run this part has had. Read by both panels: the failures belong with the button
  // that made them, the ready ones with the viewer that shows them.
  const partJobs = row && jobs
    ? jobs.filter((j) => j.itemId === row.itemId && (!piecesOnly || !!pieceOf(j)))
    : [];

  // WHETHER THE MESH IS OPEN BIG, AND IT BELONGS TO THE PAGE RATHER THAN TO THE PART.
  //
  // It was the viewer's own state, and the card below is keyed by the part — so stepping to
  // the next part with the arrow keys threw the whole subtree away and the modal with it. What
  // somebody doing that is doing is comparing: open one leg, press right, look at the other
  // one in the same view. Landing back on the page and having to find and press the expand
  // button again is the comparison broken in half.
  //
  // Held here, above the key, it survives the part changing and the new part opens in the view
  // the old one was being read in. It still closes when there is nothing to be open ON — see
  // the effect in ViewPanel, which is where "this part has no mesh" is known.
  const [expanded, setExpanded] = useState(false);

  // ITS GENERATION, the phase's badge. Nothing in this table is reachable in the demo — every button
  // on it spends — and the mesh it produced is what the demo is showing, not the machinery.
  const generator = row && !piecesOnly && !IS_DEMO && (
    <GeneratePanel
      key={row.key}
      docId={docId}
      row={row}
      // NO PRICE: the written model is billed on tokens nobody can count in advance,
      // and a figure on the button would be a claim about somebody's bill.
      model={writtenModel ?? ''}
      passChoices={passChoices(probe)}
      passes={passes}
      onPasses={setPasses}
      effort={effort}
      effortChoices={efforts}
      onEffort={setEffort}
      busy={busyKey === row.key}
      progress={busyKey === row.key ? flights[`part:${row.key}`]?.progress : undefined}
      since={busyKey === row.key ? flights[`part:${row.key}`]?.startedAt : undefined}
      disabled={!!locked || !!busyKey || !writtenModel || toolMade}
      loaded={photosLoaded}
      schema={schema}
      stepCrops={stepCrops}
      interactions={interactions}
      docDrawing={docDrawing}
      onRun={(inputs, crops) => void run(row.key, inputs, crops)}
      onStop={stopRun}
      onZoom={(src, list) => { setZoom(src); setZoomList(list ?? null); }}
    />
  );
  // THE BADGE ALONE, while one of the subphase's tables is open in place of the model (see
  // PartsPhase): the phase's badges stay whatever the strip shows.
  if (badgeOnly) return <>{generator}</>;

  return (
    <div className="flex w-full flex-col items-center gap-5">
      {/* Not where nothing can be run anyway (see ProductModelStep). */}
      {probe && !writtenModel && !IS_LOCKED && (
        <p className="text-sm text-rose-600">{t('notice.3d.noKey')}</p>
      )}
      {toolMade && <p className="max-w-xl text-center text-sm text-slate-400">{t('notice.3d.madeWithTool')}</p>}
      {error && !errorOnARow(error, jobs ?? []) && <p className="max-w-xl text-center text-sm text-rose-600">{error}</p>}
      {jobsFailed && <p className="max-w-xl text-center text-sm text-rose-600">{t('notice.3d.jobsFailed')}</p>}

      {/* The part is not in the Parts DB at all, so there is no row for a photograph or a
          mesh to hang off and nothing here can be done. The table above is where that is
          fixed, and it offers the button. */}
      {!row && (
        <p className="max-w-lg text-center text-sm text-slate-400">
          {t('notice.3d.noPhotos')}
        </p>
      )}

      {row && (
        <div key={row.key} className="flex w-full max-w-4xl flex-col gap-4">
          {/* A COMPONENT'S MESH IS A PIECE OF THE PRODUCT. Nothing to say once a piece has been
              cut: the mesh is on screen. Only its absence is worth a sentence. */}
          {piecesOnly && jobs !== null && !partJobs.some((j) => j.ready) && (
            <p className="text-center text-sm text-slate-400">
              {t('notice.models.noPiece')}
            </p>
          )}

          {/* TWO CARDS, THE MESH FIRST. They shared one white rectangle, on the reasoning
              that picking a photograph and looking at what it produced are one thing
              happening — see MeshPanel, which draws the card now and where the argument for
              splitting them is written out.

              THE ANSWER ON TOP. This card is what the phase is FOR — the part in three
              dimensions — and the one under it is the working: which photographs, how hard,
              how many passes. Opening a part to see whether it has a model should
              not begin with the machinery for making one. */}
          <ViewPanel
            corner={corner}
            repainting={repainting}
            row={row}
            docId={docId}
            partAt={partAt}
            expanded={expanded}
            onExpanded={setExpanded}
            // The colour this part's material was measured at, for the face levelling to
            // aim at. Undefined where no material is recorded or none has been sampled —
            // which the panel says, rather than quietly aiming somewhere else.
            locked={locked}
            busy={generatingHere}
            jobs={partJobs}
            known={jobs !== null}
            // THE RUN IN FLIGHT, so the strip shows a tile for it the moment the button is
            // pressed — in the row of the engine that is making it. See MeshRunStrip.
            pending={generatingHere
              ? {
                model: engineModelKey(writtenModel ?? ''),
                label: writtenModelLabel(probe, writtenModel ?? ''),
                // The newest pass this run has landed, for the tile to open. The list is
                // newest-first, so the first of them this run saved is the latest.
                made: partJobs.find((j) => j.ready
                  && flights[`part:${row.key}`]?.progress?.saved?.includes(j.id)) ?? null,
              }
              : null}
            settingsLine={settingsLine}
            engineLabel={(j) => engineLabelOf(j, probe)}
            // A PIECE FOLLOWS WHAT IT WAS CUT FROM: a component's, the product; a piece of
            // hardware's, the set it was built in — whose card below is where it is kept.
            catalog={piecesOnly
              ? { ...dbSwitch, fixed: t('notice.models.pieceFixed') }
              : { ...dbSwitch, fixed: (j: Job3D) => (pieceOf(j) ? t('notice.models.setPieceFixed') : undefined) }}
            rescalingId={rescalingId}
            deletingId={deletingId}
            onRescale={rescale}
            onDelete={(j) => void remove(j)}
            // One row replaced, not the list refetched. A repair keeps the run's id, so
            // there is nothing new to discover — and a refetch would rebuild the strip
            // under the viewer that is showing one of its meshes.
            //
            // BUT THE PARTS DB HOLDS ITS OWN COPY of every curated run, module-scope and
            // shared (see partModels), and that copy is what the assembly phase draws from.
            // It was forgotten when a run was switched on or deleted and not when one was
            // EDITED — so a part turned upright here went on being drawn on its side in
            // Phase 5, from a stale run whose version stamp still addressed the mesh as it
            // was before the turn. An edit changes the file behind the address, which is
            // exactly the thing that cache is a copy of.
            onPatch={(j) => {
              setJobs((list) => list && list.map((x) => (x.id === j.id ? j : x)));
              if (j.inCatalog) invalidatePartModels();
            }}
            // A part with NO run can now make its first one from inside the viewer's
            // empty frame, so the panel needs the same refetch the source tabs do: this
            // one is a new row, not a row to replace.
            onCreated={(job) => {
              // PAINTED AS IT IS BORN, when this part's material already has a colour. A
              // mesh nobody paints is placeholder grey, and a file cannot say whether grey
              // means "nobody chose" or "grey plastic".
              //
              // From what the part IS MADE OF — the parts table's answer for the submodel
              // on screen — and from the colour somebody sampled for that material. It
              // used to be a convention keyed on the category, with a colour I had picked
              // for felt; this is the same idea answered from the notice and from a
              // photograph instead of from a table in the code. No material, or no colour
              // sampled for it yet, and the mesh is left grey rather than given a colour
              // nobody has a reason to believe.
              //
              // Then the list is read again either way: the paint is an edit to the run,
              // and the run is new.
              //
              // A WRITTEN run — a rebuild at new dimensions — is painted the way the written
              // engine's runs are, wood first and then the colour: see paintWritten.
              const surface = wornSurface;
              void (isWrittenRun(job)
                ? paintWritten(job).catch(() => {})
                : surface?.rgb
                  ? colourJob(job.id, surface.rgb, undefined, surface.fromUrl,
                    paintedAs(worn!, surface)).catch(() => {})
                  : Promise.resolve()
              ).then(() => {
                void reloadJobs();
                invalidatePartModels();
              });
            }}
          />

          {generator}
        </div>
      )}

      {/* THE SAME MODAL THE GENERATION TABLE OPENS, on the same pictures. These candidates
          ARE that phase's output — the ones switched into the Parts DB — and zooming one
          here used to give a bare picture while zooming it three panels up gave what was
          asked of it and what it was drawn from. One picture, one answer.

          Without the delete: this panel is about choosing what to reconstruct FROM, and a
          button here that reached back into the generation history would be a different
          page's decision taken on this one. */}
      <ImageLightbox
        src={zoom}
        alt={undefined}
        srcs={zoomList ?? (row?.photos || []).map((p2) => partPhotoSrc(p2.id))}
        onNavigate={setZoom}
        details={
          zoomedEntry && (
            <GeneratedDetails entry={zoomedEntry} schematic={partSchematic} />
          )
        }
        onClose={() => setZoom(null)}
      />
    </div>
  );
}

// Making the mesh: what goes to the model. Its own box,
// because the two panels answer different questions — this one is about the INPUT, and it
// is where the wrong photograph is noticed and swapped.
function GeneratePanel({
  docId, row, model, effort, effortChoices: effortOptions, onEffort,
  passChoices: passOptions, passes, onPasses,
  busy, since, progress, disabled, loaded, schema, stepCrops, interactions, docDrawing,
  onRun, onStop, onZoom,
}: {
  // Where this part's ticked photographs are remembered — see generatorMemory.
  docId: string;
  row: PartRow;
  // THE WRITTEN MODEL THAT MAKES IT — see writtenEngineOf.
  model: string;
  // HOW HARD THE MODEL THINKS, and what the chosen one takes — see effortChoices.
  effort: string;
  effortChoices: string[];
  onEffort: (effort: string) => void;
  // HOW MANY PASSES ARE ON OFFER, what is chosen, and how to change it. The list is the
  // server's — see passChoices — because the route clamps whatever arrives to it.
  passChoices: number[];
  passes: number;
  onPasses: (passes: number) => void;
  busy: boolean;
  // Where the run is — see MeshGenerateControls.
  progress?: RunProgress;
  // When the run was pressed — see MeshGenerateControls.
  since?: number;
  disabled: boolean;
  // Whether the photographs have been READ. Before that, "this part has none" is not an
  // answer anyone has, and saying it would be saying it of every part on every open.
  loaded: boolean;
  // WHETHER THIS PART IS MODELLED FROM THE MANUAL'S DRAWING rather than from photographs —
  // hardware and tools are. It decides what the tiles offer, and it is what the prompt is told
  // so that its first sentence names its own input (see _OPENING on the server).
  schema: boolean;
  // A piece of hardware's page crops — see usePartStepCrops. Each one a tile of its own, ticked
  // or not, and every ticked one sent after the drawings.
  stepCrops: StepCropPicture[];
  // The hardware this part works with — shown in the badge's prompt as the run sends it.
  interactions: WrittenInteraction[];
  // This manual's own drawing of the part, cut from the page — see DOC_DRAWING.
  docDrawing: string | null;
  // THE PICTURES THE PRESS CHOSE, primary first, and the page crops ticked.
  onRun: (inputs: WrittenInput[], stepCrops: string[]) => void;
  // HOW TO CALL IT OFF.
  onStop: () => void;
  onZoom: (src: string, list?: string[]) => void;
}) {
  const { t } = useLanguage();
  // WHICH PHOTOGRAPHS THE RUN IS MADE FROM, in the order they were pressed — which is the
  // order the model is shown them, and the first is the primary: proportions are measured
  // against it and the texture patch is cut from it.
  //
  // As many as are ticked: what one picture cannot say — the back of a leg, how deep a seat
  // is — is exactly what a second is for. The front is the DEFAULT.
  // Kept per part, outside this panel, so leaving the part and coming back finds them ticked.
  // THE PICTURES THIS PART IS MODELLED FROM, and for a piece of hardware they are not
  // photographs.
  //
  // Nobody photographs a barrel nut for an assembly manual. What the manual has of it is the
  // drawing in its parts list, which this app already crops per part and keeps on the catalog
  // row (notice_catalog_schematics) — so that drawing is the input, and the prompt says it is a
  // schema rather than calling it a photograph (see the `schema` prop). The alternative was the
  // one being used: an image model's INVENTED photograph of a barrel nut, modelled instead of
  // the real one the manual draws.
  //
  // `view` only on a photograph. An angle is something Phase 1 classifies a listing picture as;
  // a parts-list drawing is drawn from wherever it is drawn from and claiming "front" would be
  // an angle nobody read.
  const schematics = useItemSchematics(row.itemId);
  //
  // THIS MANUAL'S DRAWING FIRST, AND IN PLACE OF WHATEVER THE ROW KEPT FROM THIS MANUAL. The row's
  // drawings are whatever was ever saved onto it, and the first was the default: a row called
  // "Screwdriver" held the PVC screwdriver's, from before the PVC one had a row of its own, and
  // the screwdriver was built from it while its card showed the right drawing. What the card
  // shows is what is built from; another manual's drawing of the same part is still a tile.
  const pool = useMemo(
    () => {
      if (!schema) return row.photos.map((p) => ({ id: p.id, src: partPhotoSrc(p.id), view: p.view }));
      const kept = (docDrawing ? schematics.filter((one) => one.docId !== docId) : schematics)
        .map((one) => ({ id: one.id, src: schematicSrc(one.id), view: undefined }));
      return docDrawing ? [{ id: DOC_DRAWING, src: docDrawing, view: undefined }, ...kept] : kept;
    },
    [schema, schematics, row.photos, docDrawing, docId],
  );
  const pickKey = `part:${row.key}`;
  const pickedIds = useGeneratorChoice(docId, pickKey).picked ?? NO_PICKS;
  const setPickedIds = (next: (was: number[]) => number[]) => setGeneratorChoice(
    docId, pickKey, { picked: next(getGeneratorChoice(docId, pickKey).picked ?? []) },
  );
  // The pressed ones that still exist, then the default — so a part with nothing pressed
  // runs on its front photograph exactly as it always did.
  const chosen = useMemo(() => {
    const kept = pickedIds
      .map((id) => pool.find((p) => p.id === id))
      .filter((p): p is (typeof pool)[number] => !!p);
    // The front is the default where the page knows one — a drawing has no front, so the
    // fallback there is simply the first drawing the part has.
    const front = pool.find((p) => p.id === row.front?.id) ?? null;
    const fallback = front ?? pool[0] ?? null;
    return kept.length ? kept : (fallback ? [fallback] : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedIds, pool, row.front]);
  // A press toggles, down to the last one standing: there is nothing to model from with no
  // picture, and the button would go dead under the pointer.
  const pick = (id: number) => setPickedIds((was) => {
    const now = was.length ? was : chosen.map((p) => p.id);
    if (!now.includes(id)) return [...now, id];
    return now.length > 1 ? now.filter((x) => x !== id) : now;
  });

  // THE PAGE CROPS TICKED, in the order they are shown. A plain toggle each, none ticked by
  // default: a crop is a picture more on every pass, and paying for it is a choice. Kept per
  // part beside the drawings' picks; a key whose crop has gone since is simply not there.
  const cropKeys = useGeneratorChoice(docId, pickKey).pickedCrops ?? NO_CROP_PICKS;
  const crops = stepCrops.filter((c) => cropKeys.includes(c.key));
  const toggleCrop = (key: string) => {
    const was = getGeneratorChoice(docId, pickKey).pickedCrops ?? [];
    setGeneratorChoice(docId, pickKey, {
      pickedCrops: was.includes(key) ? was.filter((k) => k !== key) : [...was, key],
    });
  };

  // Nothing to reconstruct FROM. The part is in the database but no photograph of it has
  // been kept yet, and the GENERATED row of the table above is where one is made and
  // switched on. The CAD side of the toggle still works — that is the whole point of it
  // for a part whose pictures never came out.
  const none = loaded && !pool.length;

  // Every tile in the row, in order — what the zoom steps through.
  const tiles = [...pool.map((p) => p.src), ...stepCrops.map((c) => c.src)];

  // Every photograph the database holds of the part, and the job is to CHOOSE one. Click a
  // picture to reconstruct from it; the chosen one is outlined and the rest are dimmed.
  // The model reads every one it is given, in order.
  const pictures = none ? (
    <p className="mb-1 text-center text-sm text-slate-400">
      {t(schema ? 'notice.3d.noSchematics' : 'notice.3d.noPhotos')}
    </p>
  ) : (
    <>
      {pool.length + stepCrops.length > 1 && (
        <p className="text-center text-xs text-slate-400">
          {t('notice.3d.pickInputs')}
        </p>
      )}
      <div className="mb-1 flex flex-wrap items-start justify-center gap-2">
        {pool.map((p) => (
          <PhotoTile
            key={p.id}
            src={p.src}
            view={p.view}
            on={chosen.some((c) => c.id === p.id)}
            // WHICH PICTURE IT IS IN THE CALL, where the call takes several. The prompt
            // numbers them — "Picture 2: from the back" — so the tile carries the same
            // number, and the row on screen IS the list the model is given.
            at={chosen.length > 1
              ? chosen.findIndex((c) => c.id === p.id) + 1 : 0}
            onZoom={(src) => onZoom(src, tiles)}
            onPick={() => pick(p.id)}
          />
        ))}
        {/* AND WHERE THE MANUAL DRAWS IT IN USE, after the drawings and numbered after them —
            the order the call shows them in. */}
        {stepCrops.map((c) => {
          const at = crops.findIndex((x) => x.key === c.key);
          return (
            <PhotoTile
              key={c.key}
              src={c.src}
              on={at >= 0}
              at={at >= 0 && chosen.length + crops.length > 1 ? chosen.length + at + 1 : 0}
              onZoom={(src) => onZoom(src, tiles)}
              onPick={() => toggleCrop(c.key)}
            />
          );
        })}
      </div>
    </>
  );

  // THE PHASE'S "3D MODEL GENERATION" BADGE, for this part — see MeshGenerateControls.
  return (
    <MeshGenerateControls
      effort={effort}
      effortChoices={effortOptions}
      onEffort={onEffort}
      passes={passes}
      passChoices={passOptions}
      onPasses={onPasses}
      busy={busy}
      progress={progress}
      since={since}
      // A PICTURE CHOSEN, A KEY CONFIGURED, THE PAGE NOT LOCKED — this panel's own list,
      // which is not the product panel's. The control draws the answer and asks nothing.
      canRun={!disabled && chosen.length > 0}
      onRun={() => onRun(chosen.map((p) => ({ id: p.id, view: p.view })), crops.map((c) => c.src))}
      onStop={onStop}
      scope="hardware"
      pictures={pictures}
      // ITS MANUAL CROPS ARE TILES IN IT, after the drawings.
      picksCrops
      call={{
        subject: row.name,
        photos: chosen.map((p) => ({ url: p.src, view: p.view })),
        model,
        size: writtenSize(row.size),
        schema,
        interactions,
        crops: crops.map((c) => ({
          part: row.name, view: 'step', url: c.src, bbox: [0, 0, 1, 1] as [number, number, number, number],
        })),
      }}
    />
  );
}

// Looking at the mesh, choosing between the runs, and keeping one. Drawn even with nothing
// in it: the box is where a model APPEARS, and a heading that comes and goes makes the
// phase's shape depend on how far along it is.
//
// One viewer for BOTH sources: a reconstruction and a converted STEP file are runs of the
// same part, listed together and compared against each other, and which of the two made
// the mesh you are looking at is the line under it, not a second panel.
// The box for a picture that is already only the part — the manual's drawing, which was cut
// out of the PDF at extraction and has no chair around it to be found in.
const WHOLE_PICTURE: HeroBox = [0, 0, 1, 1];

function ViewPanel({
  row, docId, partAt, jobs, settingsLine, engineLabel, catalog, rescalingId,
  deletingId, busy, pending, onRescale, onDelete, onPatch, onCreated,
  expanded, onExpanded, known,
  locked, repainting = false, corner,
}: {
  corner?: ReactNode;
  row: PartRow;
  // A reconstruction of THIS part is running. The viewport empties for it: see below.
  busy: boolean;
  // Where the part sits in the notice's list, so the colour menu can reach its listing
  // crops — the pictures of it on the assembled product.
  partAt?: number;
  // The manual these runs belong to. Only the repair question uses it: a component may be
  // shown the notice's photographs of the assembled product beside its own drawing.
  docId: string;
  jobs: Job3D[];
  // Whether the runs have been answered at all: until then the empty frame is a spinner, not
  // "no mesh", which is only true once the server has said so.
  known: boolean;
  // A tile's tooltip — see settingsLine.
  settingsLine: (job: Job3D) => string | undefined;
  // Which engine made a run, for the heading. A function and not the probe itself, like
  // the line above it: this panel chooses which run is on screen, so the page above cannot
  // name it — but naming it is the page's knowledge, not this panel's.
  engineLabel: (job: Job3D | null) => string | null;
  // The database switch — see useCatalogSwitch.
  catalog: CatalogSwitch;
  // The run being resized right now.
  rescalingId: number | null;
  // The run being deleted right now. Shared with the strip below the panel: one run, one
  // spinner, whichever of the two bins was pressed.
  deletingId: number | null;
  onRescale: (job: Job3D, axis: 'x' | 'y' | 'z', fit: AxisFit) => Promise<Job3D | null>;
  onDelete: (job: Job3D) => void;
  // One run, changed. A repair edits a mesh in place, so the row keeps its id and there
  // is nothing to re-read — only a row in the list to replace.
  onPatch: (job: Job3D) => void;
  // A run that did not exist a moment ago: the first shaping of a part with no mesh, made
  // from the empty frame below. A new row rather than a changed one, so the list is read
  // again instead of patched — and, before that, given the material its kind of part is
  // usually made of.
  onCreated: (job: Job3D) => void;
  // Whether this mesh is open big, and the way to say so. Held by the page above rather than
  // here: this panel is keyed by the part, and the arrow keys step through parts — see where
  // it is declared.
  expanded: boolean;
  onExpanded: (open: boolean) => void;
  // A run being made right now, and by what — drawn as a tile in the strip, which the newest
  // pass it has already landed opens. Null when nothing is in flight for this part.
  pending: Parameters<typeof MeshRunStrip>[0]['pending'];
  locked?: boolean;
  // This part's meshes are having their material rewritten — spinners in place of them.
  repainting?: boolean;
}) {
  const { t } = useLanguage();
  // The newest run is the one shown. The rest stay listed underneath — a reconstruction is
  // the kind of thing you make twice with different inputs and then compare.
  const ready = jobs.filter((j) => j.ready);
  const { shown, setShownId, showRun } = useShownRun(ready);
  // A RUN THAT MADE NOTHING, where its mesh would be — see useShownFailure.
  const failedRun = useShownFailure(jobs, busy);
  // A TILE PRESSED WHILE A RUN IS GOING shows its model — the box empties for the run (see
  // below), and the earlier models are what somebody compares the coming one against while it
  // thinks. Forgotten when the next run starts, which empties the box again.
  const [peek, setPeek] = useState(false);
  useEffect(() => { if (busy) setPeek(false); }, [busy]);
  // The shown mesh's extents in its own file units, reported by the viewer once it has
  // loaded. Cleared when the mesh changes so a scale is never computed from the box of
  // the run you were looking at a moment ago.
  const [rawSize, setRawSize] = useState<Extent | null>(null);
  // AND WHAT SHAPE IT IS INSIDE THAT BOX — see partProfile. Held beside the extents and
  // cleared with them: the two are one measurement of one mesh, and a profile left standing
  // when the run changed would place holes against the shape of a different part.
  const [rawProfile, setRawProfile] = useState<PartProfile | null>(null);
  useEffect(() => {
    setRawSize(null);
    setRawProfile(null);
  }, [shown?.id]);
  // Whether the mesh is open big. The panel's viewport is a strip across a page of other
  // things; deciding whether a reconstruction is any good means turning it over and
  // looking underneath, and that is not a thing you do in a strip.
  const setExpanded = onExpanded;
  // THE PHOTOGRAPHS THE RUN ON SCREEN WAS MADE FROM, across the top of the big viewer, and
  // nothing else. The row held the manual's drawing and the part's listing crops, which are
  // worth looking at and are not what this mesh was made from: a written run is shown the
  // photographs that were ticked. Off the run itself (see writtenInputsOf),
  // so every run already made shows its own and the row follows the runs as they are stepped
  // through. A run made from no photograph — a CAD file, a lathe — has none to show.
  const sent = useMemo(
    () => (shown && shown.source === 'photo' ? writtenInputsOf(shown) : []),
    [shown],
  );
  const sentRow = useMemo<StripCrop[]>(
    () => sent.map((one) => ({ url: partPhotoSrc(one.imageId), bbox: WHOLE_PICTURE })),
    [sent],
  );
  // The one opened large, by address; the arrows step through the same row.
  const [openSent, setOpenSent] = useState<string | null>(null);
  const stamp = meshStamp(shown);
  // WHICH STATE OF THE MESH THE VIEWER IS SHOWING, as a count of edits, or null for the
  // mesh as it stands. Pressing a dot on the rail sets it — every overwrite already keeps
  // a copy of what it replaced, so looking at what the leg was before it was shortened
  // costs a fetch and nothing else. It used to cost an Undo and a Redo with a repair in
  // between that might be refused.
  //
  // Cleared whenever the mesh moves under it — a different run, or an edit landing on this
  // one. An index counts positions in an edit stack, and a stack that has just grown or
  // shrunk makes the old number point at a different moment.
  const [viewAt, setViewAt] = useState<number | null>(null);
  // AND THE SAME FOUR FACTS TO WHAT STANDS UNDER THE CARD — the holes table, which is about
  // this mesh and is the phase's child rather than this panel's. See shownMesh.
  //
  // NOT WHILE LOOKING BACK, by the rule the controls in the footer already follow: what the
  // viewer measured while it is showing an earlier state is that state's box, and every hole
  // placed against it would be placed on a part that has been edited since.
  usePublishShownMesh(shown ?? null, viewAt == null ? rawSize : null, row.size,
    viewAt == null ? rawProfile : null, onPatch);
  useEffect(() => setViewAt(null), [shown?.id, stamp]);
  // What the viewer is handed, live or historical. The edit count on the live one is a
  // cache buster: a repair overwrites the file at that address, and without it the viewer
  // would be given the mesh from before. A past state needs none — its file is written
  // once and never again.
  const shownSrc = shown
    ? (viewAt == null ? meshSrc(shown.id, stamp) : meshAtSrc(shown.id, viewAt, stamp))
    : '';

  // Nothing left to be expanded ON. Closing on any change of shown run was the rule
  // before, which was right while a delete and a repair were the only two things that
  // could change it — and wrong the moment the arrow keys made it something you do on
  // purpose. A repair now leaves the modal open on its result, which is where you would
  // want to look at it anyway.
  useEffect(() => { if (!ready.length) setExpanded(false); }, [ready.length]);

  // WHICH VIEW THE TWO VIEWERS SHARE. The run — its last pass's id, which every pass of it
  // agrees on — so a draft and its correction are one thing to look at, and so are the
  // panel's viewer and the modal's. Both are handed THIS, because a key computed twice is a
  // key that stops matching the day one of the two is edited. See PartViewer's keepView.
  const viewKey = shown ? String(passGroup(shown, ready).slice(-1)[0]?.id ?? shown.id) : undefined;



  // The heading over the mesh — the panel's, and the modal's when it is opened big.
  const viewTitle = panelTitle(t('notice.3d.boxView'), withEffort(engineLabel(shown), shown));

  return (
    <MeshPanel title={viewTitle} corner={corner}>
      {/* A RUN IN FLIGHT EMPTIES THIS BOX. It went on showing the part's last mesh for the
          thirty to sixty seconds a reconstruction takes — under the heading "3D model",
          beside a button saying it was generating one — so the panel was answering the
          question being asked with the previous answer to it. Somebody watching for the new
          mesh to arrive has no way to tell it already has.

          The frame stays and the mesh goes: same box, same size, a spinner where the model
          will be. Nothing below moves. The runs strip underneath keeps its thumbnails, which
          is right — those are history and say so — and pressing one shows it (`peek`). */}
      {busy && !peek ? (
        <MeshPlaceholder
          label={t('notice.3d.generating')}
          icon={<Loader2 className="h-8 w-8 animate-spin text-slate-300" />}
          className="h-48 w-full"
        />
      ) : failedRun.failure ? (
        <FailedRunPanel
          job={failedRun.failure}
          className={VIEWER_BOX}
          deleting={deletingId === failedRun.failure.id}
          onDelete={() => onDelete(failedRun.failure!)}
          badge={(
            <WrittenCallBadge
              subject={row.name}
              title={t('notice.3d.runFailedCall')}
              run={failedRun.failure}
              runs={[failedRun.failure]}
              model={(failedRun.failure.model || '').replace('openai/', '')}
              size={writtenSizeOf(failedRun.failure)}
            />
          )}
        />
      ) : shown ? (
        <div className="flex flex-col items-center gap-2">
          {/* Torn down while the modal is open. A viewer is a WebGL CONTEXT and a copy of
              the mesh on the GPU, a browser grants a page a fixed number of the first, and
              this page already spends several on the thumbnail strip — so the expanded
              view was asking for one more while an identical scene sat behind it, invisible
              under a full-screen overlay. On a 460,000-triangle reconstruction that is two
              of the largest things on the page to look at one of them.

              AND IT NO LONGER COSTS THE VIEW. Unmounting used to lose how the part was being
              looked at — the modal framed it afresh, and closing it framed it afresh again.
              Both viewers now keep the camera and the pose under one key (see viewKey), so
              the tear-down is what it was meant to be: a context freed, and nothing else. */}
          {/* AND TORN DOWN WHILE THE RETOUCH IS OPEN, for the reason the modal already tears
              it down: a viewer is a WebGL context and a copy of the mesh on the GPU, and it
              goes on rendering the same 475,000 triangles sixty times a second underneath a
              full-screen overlay nobody can see it through. The retouch has a viewer of its
              own, and turning THAT one was stuttering against this one's frames. */}
          {/* THE ASSEMBLY PHASE'S OWN SCENE, with one part in it and nothing moving — see
              PartViewer. A mesh judged here and assembled two phases later has to look the
              same in both, and it was not: two gizmos, two readouts of where the camera was
              standing, two ideas of what a measurement looks like.

              No draw modes and no triangle count in the phase's own panel either way. Here
              the question is "is this the right part, and is it the right size"; how the mesh
              is BUILT is the bench pages' question, and the expanded viewer's. */}
            <MeshStage
              busy={repainting}
              src={shownSrc}
              // The part's name, in the viewer's own legend — the same one the panel-sized
              // viewer above is given. Without it the legend read "3D model", which is what
              // every mesh in this app is, in the one view where the object fills the screen
              // and is worth naming.
              label={row.name}
              // ONE VIEW ACROSS THE TWO VIEWERS AND ACROSS A RUN'S PASSES. The key is the
              // run — the last pass's id, which every pass of it agrees on — so toggling
              // between them keeps the camera, expanding keeps it, and stepping to another
              // run frames afresh. Which is the whole point of the toggle: two meshes seen
              // from two angles cannot be compared.
              keepView={viewKey}
              expanded={expanded}
              onExpanded={setExpanded}
              // WHICH PASS YOU ARE LOOKING AT, and the way to another. Drawn by MeshStage,
              // so the whole product's viewer has it too rather than waiting for somebody to
              // write the same toggle again.
              passes={{ of: ready, shown, onShow: setShownId, onDelete, deletingId }}
              heading={viewTitle}
              took={generationTook(shown, ready)}
              stages={generationStages(shown, ready)}
              checks={checksOf(shown)}
              className={VIEWER_BOX}
              // WHAT WAS ASKED AND WHAT CAME BACK, and nothing else.
              //
              // It carried two more lines and both are gone: "fal defaults — mesh #100 (from
              // AIimg #26)", which said three things the page says elsewhere, and
              // "← mesh 4 / 11 →", this mesh's place among the part's. The arrow keys still
              // step through them; what has gone is the line saying so, which is the trade —
              // the room over the viewer is worth more than the count of an unread number.
              caption={
                <>
                  {/* WHAT WAS ASKED AND WHAT CAME BACK, for THIS mesh. In the caption
                      because the caption is where the run says which one it is, and the
                      call is the rest of that sentence: eleven meshes in the strip are
                      eleven different calls, and stepping through them with the arrow keys
                      steps through those too. The badge beside the generate button answers
                      about the newest run and cannot answer about this one. */}
                  {isWrittenRun(shown) && (
                    <span className="ml-1.5 inline-flex translate-y-0.5 align-middle">
                      <WrittenCallBadge
                        subject={row.name}
                        // WHAT THIS RUN WAS SHOWN, off the run — not what the panel would
                        // send now. A badge in the viewer is about a call already made, and
                        // the pictures it was given are recorded on it.
                        photos={shown.source === 'photo'
                          ? writtenInputsOf(shown).map((one) => ({
                            url: partPhotoSrc(one.imageId), view: one.view,
                          }))
                          : []}
                        run={shown}
                        runs={ready}
                        model={(shown.model || '').replace('openai/', '')}
                        size={writtenSizeOf(shown)}
                      />
                    </span>
                  )}
                  {/* The id in parentheses after what the mesh was made from, and the
                      WORD with it. A bare "#33" under a manual that numbers its own runs
                      1 to 12 is a number in a page full of numbers; "mesh #33" is the one
                      thing it can be.

                      It sat alone at the top for a while, beside the delete: two facts
                      about the same mesh, one of which is a number you quote and the other
                      a button you press, and putting them together made the id read as a
                      label for the button. It belongs with the rest of what this mesh IS. */}
                </>
              }
              dims={row.size}
              estimated={row.size?.source === 'derived'}
              trueScale={isTrueScale(shown)}
              // AND WHAT THIS RUN MEASURED, for a part the manual never dimensioned: a written
              // build is read back in millimetres the moment Blender has run the script, so a
              // barrel nut nobody has measured still knows it is 10 mm across. Last of the
              // three — see mmScale, where the drawing comes first.
              builtMm={builtBoxOf(shown)}
              // WHICH RUN THIS IS, so the box's six words can carry what the orientation call
              // said about each of them — the controls in the footer below are what ask, and
              // the answer belongs on the faces it is about. See PartViewer.
              orientJobId={shown.id}
              sides={row.pair}
              onMeasure={setRawSize}
              onProfile={setRawProfile}
              job={shown}
              catalog={catalog}
              // The photographs this run was made from, across the top of the viewer.
              strip={
                <CropStrip
                  crops={sentRow}
                  alt={row.name}
                  onOpen={(crop) => setOpenSent(crop.url)}
                />
              }
              // The scaling controls live HERE now. Fitting an axis to a measurement is a
              // judgement about geometry, and it was being offered under a viewer too
              // small to make it in: what you have to read to decide is the arrow's two
              // numbers, the drawing's and the mesh's, side by side.
              footer={
                <div className="flex w-full flex-col items-center gap-5">
                {/* THE NUMBERS A WRITTEN MODEL WAS BUILT FROM, and a rebuild at changed ones
                    — see WrittenDimensionsPanel. The rebuilt run is new, so it arrives the
                    way any new mesh here does. */}
                {isWrittenRun(shown) && dimensionsOf(shown).length > 0 && (
                  <WrittenDimensionsPanel
                    job={shown}
                    disabled={!!locked}
                    onRebuilt={(job) => {
                      onCreated(job);
                      setShownId(job.id);
                    }}
                  />
                )}
                <MeshControls
                  job={shown}
                  docId={docId}
                  // NOT WHILE LOOKING BACK. The viewer measures whatever it is showing,
                  // and while that is an earlier state the box it reports is that state's —
                  // but every scale button acts on the mesh as it STANDS. Fitting an axis
                  // to a measurement taken off the mesh from three edits ago is a wrong
                  // number arrived at silently, so the controls are handed no measurement
                  // at all, which is the case they already grey themselves for.
                  size={viewAt == null ? rawSize : null}
                  // AND BY THE SAME RULE, for the same reason: a profile taken off the mesh
                  // as it was three edits ago would centre a bore on wood that has been cut
                  // away since.
                  profile={viewAt == null ? rawProfile : null}
                  dims={row.size}
                  viewingAt={viewAt}
                  onViewAt={setViewAt}
                  rescaling={rescalingId === shown.id}
                  onRescale={(axis, fit) => {
                    void onRescale(shown, axis, fit).then((made) => made && setShownId(made.id));
                  }}
                  category={row.category}
                  // Where a colour comes from: the part's listing crops, and only those. A
                  // generated photograph is a guess at the object in light it invented, and a
                  // colour read off one would be filed against a material the retailer names
                  // and sells. A part with no crop has no colour to offer, and says so.
                  partAt={partAt}
                  onChanged={onPatch}
                />
                </div>
              }
              // Deleting a run belongs in this modal because it is the only place the mesh
              // is big enough to judge, and "this one is not worth keeping" is a judgement
              // about what is on screen. It sits in the corner OPPOSITE the close, out of
              // the row of controls pressed to make the mesh better — it was the only thing
              // in that row that made nothing better.
              //
              // No confirmation, matching the strip below. A run is re-creatable and the
              // thing being deleted fills the screen; a dialog asking "are you sure" would
              // be asking about the object you are looking straight at.
              corner={
                <DeleteRunButton
                  label={t('notice.3d.deleteModel')}
                  busy={deletingId === shown.id}
                  onClick={() => { setExpanded(false); onDelete(shown); }}
                />
              }
            />
        </div>
      ) : !known ? (
        <MeshPlaceholder
          label={t('common.loading')}
          icon={<Loader2 className="h-8 w-8 animate-spin text-slate-300" />}
          className="h-48 w-full"
        />
      ) : (
        /* NO MESH YET, drawn as the mesh that is missing. It is made by the phase's 3D model
           generation badge, over the selector. */
        <MeshPlaceholder label={t('notice.3d.noMesh')} className="h-48 w-full" />
      )}

      {/* Every run this part has had, each drawn as the mesh it produced. A reconstruction
          is made twice with different inputs and then compared, and comparing them was the
          one thing a row of numbered pills could not help with.

          Drawn for a single run too, now that each tile carries the switch that keeps its
          mesh: the row was the comparison and is the KEEPING as well, and a part with one
          reconstruction is exactly the part that needs to say whether it kept it. */}
      {/* A PHOTOGRAPH THE RUN WAS MADE FROM, LARGE — the arrows step through the rest of what it
          was sent, named by the angle it was sent as. */}
      <ImageLightbox
        src={openSent}
        srcs={sentRow.map((c) => c.url)}
        view={(() => {
          const at = sent.find((one) => partPhotoSrc(one.imageId) === openSent);
          return at?.view ? directionLabel(at.view) : undefined;
        })()}
        onNavigate={setOpenSent}
        alt={row.name}
        onClose={() => setOpenSent(null)}
      />

      {/* Every mesh this part has had, under the one it is showing. The whole product's
          panel draws the same strip from the same component, database switch and all. */}
      <MeshRunStrip
        ready={ready}
        shown={shown}
        repainting={repainting}
        pending={pending}
        engineLabel={engineLabel}
        title={settingsLine}
        onSelect={(j) => { setPeek(true); failedRun.leave(); setShownId(j.id); }}
        onOpenRun={(j) => { setPeek(true); failedRun.leave(); showRun(j); }}
        onOpenPending={() => setPeek(false)}
        failed={failedRun.failures}
        failedShown={failedRun.failure}
        onShowFailed={(j) => { setPeek(true); failedRun.show(j); }}
        catalog={catalog}
      />

    </MeshPanel>
  );
}

// One candidate photograph: click it to make it the run's input, or zoom it from the
// corner control. The picture is the button and zoom is the corner, the same swap the
// context row of the regenerate dialog makes for the same reason — looking closely at a
// candidate must not be mistaken for choosing it.
function PhotoTile({ src, view, on, at = 0, onZoom, onPick }: {
  // THE PICTURE'S OWN ADDRESS, and not its id. A part's inputs are its photographs for a
  // component and the manual's DRAWINGS for a piece of hardware (see GeneratePanel), and those
  // are two tables with two endpoints — so the tile is told where its bytes are rather than
  // deciding from a kind it would have to be told anyway.
  src: string;
  // The angle the page knows this picture to be of, where it knows one. A drawing has none.
  view?: string;
  on: boolean;
  // ITS PLACE IN THE CALL, 1-based, or 0 where the call takes one picture and a number
  // would be answering a question nobody has.
  at?: number;
  onZoom: (src: string) => void;
  onPick: () => void;
}) {
  const { t } = useLanguage();
  const border = on ? PICKED : 'border-slate-200 bg-white opacity-50';
  return (
    <figure className="flex w-16 flex-col items-center gap-0.5">
      <div className="relative">
        <button
          type="button"
          onClick={onPick}
          aria-pressed={on}
          title={t('notice.3d.pickInputs')}
          className={`block h-16 w-16 rounded-lg border-2 p-0.5 transition-opacity hover:opacity-100 ${border}`}
        >
          <img src={src} alt="" className="h-full w-full object-contain" />
        </button>
        {/* ITS NUMBER IN THE CALL, on the picture. The prompt calls them "Picture 1",
            "Picture 2" and names an angle for each, so the row on screen is the list the
            model is given — and the order is the order they were pressed in, which is the
            only way to say "this one first" without a second control for it. */}
        {at > 0 && (
          <span className="absolute -left-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white shadow-sm">
            {at}
          </span>
        )}
        <button
          type="button"
          onClick={() => onZoom(src)}
          title={t('notice.pdf.zoom')}
          aria-label={t('notice.pdf.zoom')}
          className="absolute -right-1 -top-1 rounded-md border border-slate-300 bg-white/90 px-1 py-0.5 text-slate-500 shadow-sm backdrop-blur transition-colors hover:border-blue-500 hover:text-blue-600"
        >
          <ZoomIn className="h-3 w-3" />
        </button>
      </div>
      {/* WHICH SIDE IT SHOWS, under it. It is a fact about the photograph that the model is
          now TOLD — several pictures are sent as a numbered list naming the angle of each —
          so it has to be legible here, where somebody is choosing between them. Nothing at
          all where nobody has classified the picture: "Other" under three of four tiles is
          a column of noise, and the prompt does not claim an angle for one either. */}
      {view && (
        <figcaption className="text-center text-[10px] leading-tight text-slate-500">
          {directionLabel(view)}
        </figcaption>
      )}
    </figure>
  );
}
